import {
  AvailableIntegration,
  WorkspaceIntegrationSelection,
} from "../common/types";
import { catalogKeyForLegacyType } from "../services/integrationCatalog";
import { IntegrationCatalogEntry } from "../types/integrationCatalog";

export interface WorkspaceIntegrationRow {
  available: AvailableIntegration;
  catalog: IntegrationCatalogEntry;
  selection?: WorkspaceIntegrationSelection;
}

export const isBKGIntegrationKey = (key?: string | null): boolean =>
  key === "bkg" || key === "bkg_data_access";

const selectionForAvailable = (
  available: AvailableIntegration,
  selections: WorkspaceIntegrationSelection[],
) =>
  selections.find(
    (selection) => selection.organization_integration_id === available.id,
  ) ||
  selections.find((selection) => selection.id === available.selection_id) ||
  undefined;

export const buildWorkspaceIntegrationRows = (
  availableIntegrations: AvailableIntegration[],
  catalogEntries: IntegrationCatalogEntry[],
  selections: WorkspaceIntegrationSelection[],
) => {
  const catalogByOrgIntegrationId = new Map<string, IntegrationCatalogEntry>();
  const catalogByKey = new Map<string, IntegrationCatalogEntry>();

  catalogEntries.forEach((entry) => {
    if (entry.org_integration_id) {
      catalogByOrgIntegrationId.set(entry.org_integration_id, entry);
    }
    catalogByKey.set(entry.key, entry);
  });

  return availableIntegrations
    .map<WorkspaceIntegrationRow | null>((available) => {
      const catalog =
        catalogByOrgIntegrationId.get(available.id) ||
        catalogByKey.get(catalogKeyForLegacyType(available.type));

      if (!catalog) return null;
      if (catalog.archetype !== "org_workspace") return null;
      if (catalog.status && catalog.status !== "active") return null;
      if (catalog.available === false) return null;
      if (catalog.org_enabled === false) return null;

      return {
        available,
        catalog,
        selection: selectionForAvailable(available, selections),
      };
    })
    .filter((row): row is WorkspaceIntegrationRow => row !== null)
    .sort((a, b) =>
      (a.catalog.display_name || a.available.name).localeCompare(
        b.catalog.display_name || b.available.name,
      ),
    );
};

// Integrations with their own connect flow or a workspace-level form. Everything
// else whose catalog entry declares no workspace fields is simply on or off.
const KEYS_WITH_OWN_WORKSPACE_FLOW = new Set([
  "email",
  "deltek",
  "sharepoint",
  "url_crawler",
]);

export const needsNoWorkspaceConfiguration = (
  row: WorkspaceIntegrationRow,
): boolean => {
  const key = row.catalog.key;
  if (KEYS_WITH_OWN_WORKSPACE_FLOW.has(key) || isBKGIntegrationKey(key)) {
    return false;
  }
  const schema = row.catalog.workspace_schema;
  if (schema?.["x-widget"]) return false;
  return Object.keys(schema?.properties || {}).length === 0;
};

export const hasWorkspaceConfiguration = (
  row: WorkspaceIntegrationRow,
): boolean => {
  const selection = row.selection;
  if (!selection || !selection.enabled) return false;

  if (row.catalog.key === "email" || needsNoWorkspaceConfiguration(row)) {
    return true;
  }

  if (isBKGIntegrationKey(row.catalog.key)) {
    return Boolean(
      selection.source_workspace_id ||
      selection.workspace_config?.source_workspace_id,
    );
  }

  const workspaceConfig = selection.workspace_config || {};
  return Object.entries(workspaceConfig).some(([, entryValue]) => {
    if (Array.isArray(entryValue)) return entryValue.length > 0;
    if (entryValue && typeof entryValue === "object") {
      return Object.keys(entryValue).length > 0;
    }
    return entryValue !== undefined && entryValue !== null && entryValue !== "";
  });
};

export const supportsSync = (row: WorkspaceIntegrationRow): boolean =>
  row.catalog.key === "sharepoint" || row.catalog.key === "url_crawler";
